import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFont } from '@fortawesome/fontawesome-free-solid';
import {
    ContentState,
    EditorState,
    Modifier,
    RichUtils,
    SelectionState,
} from 'draft-js';

export const COLOR_STYLE_PREFIX = 'COLOR_';

export const createColorStyle = (color: string) =>
    `${COLOR_STYLE_PREFIX}${color}`;

export const getColorFromStyle = (styleName: string) =>
    styleName.startsWith(COLOR_STYLE_PREFIX)
        ? styleName.substring(COLOR_STYLE_PREFIX.length)
        : null;

const TEXT_COLOR_PRESETS = [
    '#272727',
    '#888f96',
    '#d92550',
    '#fd7e14',
    '#f7b924',
    '#3ac47d',
    '#17a2b8',
    '#16aaff',
    '#545cd8',
    '#6f42c1',
    '#e83e8c',
    '#795548',
];

const DEFAULT_TEXT_COLOR = '#696a78';
const INACTIVE_CONTROL_COLOR = '#7e7e7e';
const ACTIVE_CONTROL_BACKGROUND = '#e2e2e2';
const CONTROL_BACKGROUND = '#f4f4f4';

const collectColorStylesInSelection = (
    contentState: ContentState,
    selection: SelectionState
) => {
    const colorStyles = new Set<string>();
    const startKey = selection.getStartKey();
    const endKey = selection.getEndKey();
    let blockKey = startKey;
    let block = contentState.getBlockForKey(startKey);

    while (block) {
        const startOffset =
            blockKey === startKey ? selection.getStartOffset() : 0;
        const endOffset =
            blockKey === endKey ? selection.getEndOffset() : block.getLength();

        for (
            let characterIndex = startOffset;
            characterIndex < endOffset;
            characterIndex++
        ) {
            block.getInlineStyleAt(characterIndex).forEach((styleName) => {
                if (styleName.startsWith(COLOR_STYLE_PREFIX)) {
                    colorStyles.add(styleName);
                }
            });
        }

        if (blockKey === endKey) {
            break;
        }
        block = contentState.getBlockAfter(blockKey);
        blockKey = block?.getKey();
    }

    return colorStyles;
};

export interface TextColorControlProps {
    editorState: EditorState;
    onEditorStateChange(editorState: EditorState): void;
}

export const TextColorControl = (props: TextColorControlProps) => {
    const [isPaletteVisible, setIsPaletteVisible] = useState(false);

    const currentColorStyle = props.editorState
        .getCurrentInlineStyle()
        .find((styleName) => styleName.startsWith(COLOR_STYLE_PREFIX));
    const currentColor = currentColorStyle
        ? getColorFromStyle(currentColorStyle)
        : null;

    const applyTextColor = (color: string | null) => {
        const selection = props.editorState.getSelection();
        let nextEditorState = props.editorState;
        const targetStyle = color ? createColorStyle(color) : null;

        if (selection.isCollapsed()) {
            nextEditorState
                .getCurrentInlineStyle()
                .filter(
                    (styleName) =>
                        styleName.startsWith(COLOR_STYLE_PREFIX) &&
                        styleName !== targetStyle
                )
                .forEach((styleName) => {
                    nextEditorState = RichUtils.toggleInlineStyle(
                        nextEditorState,
                        styleName
                    );
                });
            if (
                targetStyle &&
                !nextEditorState.getCurrentInlineStyle().has(targetStyle)
            ) {
                nextEditorState = RichUtils.toggleInlineStyle(
                    nextEditorState,
                    targetStyle
                );
            }
        } else {
            let contentState = nextEditorState.getCurrentContent();
            collectColorStylesInSelection(contentState, selection).forEach(
                (styleName) => {
                    contentState = Modifier.removeInlineStyle(
                        contentState,
                        selection,
                        styleName
                    );
                }
            );
            if (targetStyle) {
                contentState = Modifier.applyInlineStyle(
                    contentState,
                    selection,
                    targetStyle
                );
            }
            nextEditorState = EditorState.push(
                nextEditorState,
                contentState,
                'change-inline-style'
            );
            nextEditorState = EditorState.forceSelection(
                nextEditorState,
                selection
            );
        }

        props.onEditorStateChange(nextEditorState);
    };

    const selectPresetColor = (
        event: React.MouseEvent,
        color: string | null
    ) => {
        event.preventDefault();
        event.stopPropagation();
        applyTextColor(color);
        setIsPaletteVisible(false);
    };

    return (
        <>
            <div
                className="inline-rich-text-tool-control-bg"
                style={{
                    backgroundColor: isPaletteVisible
                        ? ACTIVE_CONTROL_BACKGROUND
                        : CONTROL_BACKGROUND,
                }}
                onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setIsPaletteVisible(!isPaletteVisible);
                }}>
                <div className="text-color-control-icon" title="Text color">
                    <FontAwesomeIcon
                        icon={faFont as any}
                        style={{
                            backgroundColor: 'inherit',
                            color: INACTIVE_CONTROL_COLOR,
                        }}
                    />
                    <div
                        className={
                            'text-color-control-underline' +
                            (currentColor ? '' : ' no-color')
                        }
                        style={
                            currentColor
                                ? { backgroundColor: currentColor }
                                : undefined
                        }
                    />
                </div>
            </div>
            {isPaletteVisible && (
                <div className="text-color-palette">
                    <div
                        className="text-color-swatch"
                        title="Default"
                        style={{ backgroundColor: DEFAULT_TEXT_COLOR }}
                        onMouseDown={(event) => selectPresetColor(event, null)}
                    />
                    {TEXT_COLOR_PRESETS.map((presetColor) => (
                        <div
                            key={presetColor}
                            className="text-color-swatch"
                            title={presetColor}
                            style={{ backgroundColor: presetColor }}
                            onMouseDown={(event) =>
                                selectPresetColor(event, presetColor)
                            }
                        />
                    ))}
                    <label
                        className="text-color-custom"
                        title="Custom color"
                        onMouseDown={(event) => event.stopPropagation()}>
                        <input
                            type="color"
                            value={currentColor ?? DEFAULT_TEXT_COLOR}
                            onChange={(event) =>
                                applyTextColor(event.target.value)
                            }
                        />
                    </label>
                </div>
            )}
        </>
    );
};
